import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { gql } from '@apollo/client';
import apolloClient from '../../graphql/apollo-client';
import { CREATE_CLIENT_BRAND } from '../../graphql/mutations';
import { INTEREST_CATEGORIES } from '../../constants/categories';
import { COUNTRIES } from '../../constants/countries';
import AddressPicker, { AddressPickerValue, StructuredExtras } from '../common/AddressPicker';

interface BrandFormData {
  brandName: string;
  industry: string;
  country: string;
  city: string;
  location: string;
  latitude: number | null;
  longitude: number | null;
  website: string;
  contactPerson: string;
  contactEmail: string;
  contactPhone: string;
}

const EMPTY: BrandFormData = {
  brandName: '', industry: '', country: '', city: '', location: '',
  latitude: null, longitude: null,
  website: '', contactPerson: '', contactEmail: '', contactPhone: '',
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (brand: any) => void;
}

const AddBrandModal: React.FC<Props> = ({ isOpen, onClose, onCreated }) => {
  const { t } = useTranslation(['profile', 'common']);
  const [form, setForm] = useState<BrandFormData>(EMPTY);
  const [saving, setSaving] = useState(false);

  const handleAddressChange = useCallback((next: AddressPickerValue, extras?: StructuredExtras) => {
    setForm(prev => {
      const draft: BrandFormData = {
        ...prev,
        location: next.text,
        latitude: next.latitude,
        longitude: next.longitude,
      };
      // Country and city are read-only and only populated from the
      // resolved Google address. Keep them in lockstep with what the
      // address picker hands back.
      if (extras) {
        if (extras.country) {
          const code = extras.country.toLowerCase();
          if ((COUNTRIES as readonly string[]).includes(code)) {
            draft.country = code;
          }
        }
        if (extras.city) {
          draft.city = extras.city;
        }
      }
      return draft;
    });
  }, []);

  if (!isOpen) return null;

  const isValid = form.brandName && form.industry && form.country && form.location;

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    try {
      const res = await apolloClient.mutate({
        mutation: gql`${CREATE_CLIENT_BRAND}`,
        variables: { input: form },
      });
      const result = res.data?.createClientBrand;
      if (result?.success) {
        onCreated(result.data);
        setForm(EMPTY);
        onClose();
      }
    } catch (err) {
      console.error('Failed to create brand:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">{t('profile:myProfile.clientBrands.brandInfo')}</h2>
        </div>
        <div className="px-6 py-4 space-y-5">
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('profile:myProfile.clientBrands.requiredInfo')}</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile:myProfile.clientBrands.brandNameLabel')} <span className="text-red-500">*</span></label>
                <input type="text" value={form.brandName} onChange={e => setForm({...form, brandName: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile:myProfile.clientBrands.industryLabel')} <span className="text-red-500">*</span></label>
                <select value={form.industry} onChange={e => setForm({...form, industry: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500">
                  <option value="">{t('common:select')}</option>
                  {INTEREST_CATEGORIES.map(cat => (<option key={cat} value={cat}>{t(`common:categories.${cat}`)}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile:myProfile.clientBrands.countryLabel')} <span className="text-red-500">*</span></label>
                {/*
                  Read-only: country is set automatically from the
                  resolved address (Google Places). Manual override is
                  intentionally disabled to keep country in sync with
                  the actual location.
                */}
                <select value={form.country} disabled aria-readonly="true"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed">
                  <option value="">{t('common:select')}</option>
                  {COUNTRIES.map(c => (<option key={c} value={c}>{t(`common:countries.${c}`)}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common:visitCity', { defaultValue: '도시' })}</label>
                <input type="text" value={form.city} readOnly aria-readonly="true"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile:myProfile.clientBrands.locationLabel')} <span className="text-red-500">*</span></label>
                <AddressPicker
                  mode="single"
                  value={{
                    text: form.location,
                    latitude: form.latitude,
                    longitude: form.longitude,
                  }}
                  onChange={handleAddressChange}
                />
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('profile:myProfile.clientBrands.additionalInfo')}</h3>
            <div className="space-y-3">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('profile:myProfile.clientBrands.websiteLabel')}</label>
                <input type="text" value={form.website} onChange={e => setForm({...form, website: e.target.value})} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('profile:myProfile.clientBrands.contactPersonLabel')}</label>
                <input type="text" value={form.contactPerson} onChange={e => setForm({...form, contactPerson: e.target.value})} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('profile:myProfile.clientBrands.contactEmailLabel')}</label>
                <input type="email" value={form.contactEmail} onChange={e => setForm({...form, contactEmail: e.target.value})} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('profile:myProfile.clientBrands.contactPhoneLabel')}</label>
                <input type="tel" value={form.contactPhone} onChange={e => setForm({...form, contactPhone: e.target.value})} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500" /></div>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">{t('common:cancel')}</button>
          <button type="button" onClick={handleSave} disabled={!isValid || saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
            {saving ? t('common:saving') : t('common:save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddBrandModal;
